import { useEffect, useMemo, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";


interface BlurTextScrollProps {
  text: string;
  className?: string;
  animateBy?: "words" | "letters";
  direction?: "top" | "bottom";
  /** scrub speed; true = tied 1:1 to scroll */
  scrub?: boolean | number;
  /** start position for ScrollTrigger */
  start?: string;
  /** end position for ScrollTrigger */
  end?: string;
  stagger?: number;
}

export default function BlurTextScroll({
  text,
  className = "",
  animateBy = "words",
  direction = "top",
  scrub = true,
  start = "top 85%",
  end = "top 40%",
  stagger = 0.08,
}: BlurTextScrollProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const elements = useMemo(
    () => (animateBy === "words" ? text.split(" ") : text.split("")),
    [text, animateBy],
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const targets = el.querySelectorAll<HTMLElement>("[data-blur-segment]");
    const yFrom = direction === "top" ? -40 : 40;

    // If scrub is requested, use GSAP ScrollTrigger (scroll-tied).
    if (scrub) {
      gsap.registerPlugin(ScrollTrigger);
      const ctx = gsap.context(() => {
        gsap.fromTo(
          targets,
          { filter: "blur(10px)", opacity: 0, y: yFrom },
          {
            filter: "blur(0px)",
            opacity: 1,
            y: 0,
            ease: "power2.out",
            stagger,
            scrollTrigger: { trigger: el, start, end, scrub },
          },
        );
      }, el);
      return () => ctx.revert();
    }

    // Otherwise use IntersectionObserver — robust across viewport sizes / fullscreen.
    gsap.set(targets, { filter: "blur(10px)", opacity: 0, y: yFrom });

    let played = false;
    const play = () => {
      if (played) return;
      played = true;
      gsap.to(targets, {
        filter: "blur(0px)",
        opacity: 1,
        y: 0,
        ease: "power2.out",
        duration: 0.8,
        stagger,
      });
    };

    const checkVisible = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      if (rect.top < vh * 0.95 && rect.bottom > 0) {
        play();
        return true;
      }
      return false;
    };

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            play();
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0, rootMargin: "0px" },
    );
    io.observe(el);

    const onScrollOrResize = () => {
      if (checkVisible()) {
        window.removeEventListener("scroll", onScrollOrResize);
        window.removeEventListener("resize", onScrollOrResize);
        io.disconnect();
      }
    };
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);
    requestAnimationFrame(() => checkVisible());

    // Hard fallback so text is never permanently blurred on any viewport.
    const fallback = window.setTimeout(() => play(), 2500);

    return () => {
      io.disconnect();
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
      window.clearTimeout(fallback);
    };
  }, [direction, scrub, start, end, stagger, text]);

  return (
    <div ref={containerRef} className={className} style={{ display: "inline-block" }}>
      {elements.map((segment, index) => (
        <span
          key={index}
          data-blur-segment
          style={{ display: "inline-block", willChange: "transform, filter, opacity" }}
        >
          {segment === " " ? "\u00A0" : segment}
          {animateBy === "words" && index < elements.length - 1 ? "\u00A0" : ""}
        </span>
      ))}
    </div>
  );
}
